import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { environment } from '../../../../environments/environments';
import {
  AceTranslatePipe,
  BreadcrumbComponent,
  ButtonComponent,
  InputComponent,
  TabPanelComponent,
  TabViewComponent,
  TranslationService,
} from '@ace/ace';
import { ActivatedRoute, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { catchError, forkJoin, of, Observable } from 'rxjs';
import {
  AssessmentDto,
  AssessmentQuestionDto,
  AnswerOptionDto,
  LearnerAssessmentResultDto,
  LearnerAttemptQuestionDto,
  LearnerQuestionResponseDto,
  LearnerFileQuestionResponseDto,
  HomeworkAttachmentDto,
  QuestionBankItemDto
} from '../../../models/instructor.models';
import { AssessmentService } from '../../../services/assessment.service';
import { InstructorService } from '../../../services/instructor.service';
import { normalizeAssessmentScoreToMarks } from '../../../shared/utils/assessment-score.util';
import { katex } from '../../../shared/utils/katex-loader.util';
import { MetricCardItem, MetricCardsComponent } from '../../../shared/components/metric-cards/metric-cards.component';
import { GradingRichContentComponent } from './grading-rich-content.component';

@Component({
  selector: 'app-grading',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, AceTranslatePipe, ButtonComponent, BreadcrumbComponent, InputComponent, TabViewComponent, TabPanelComponent, MetricCardsComponent, GradingRichContentComponent],
  templateUrl: './grading.component.html',
  styleUrls: ['./grading.component.scss']
})
export class GradingComponent implements OnInit
{
  private readonly displayContentCache = new Map<string, string>();
  private readonly richContentCache = new Map<string, boolean>();

  assessmentId = '';
  resultId = '';

  assessment: AssessmentDto | null = null;
  result: LearnerAssessmentResultDto | null = null;
  attemptQuestions: LearnerAttemptQuestionDto[] = [];
  responses: LearnerQuestionResponseDto[] = [];
  fileResponses: LearnerFileQuestionResponseDto[] = [];
  attachments: HomeworkAttachmentDto[] = [];

  // Enriched rows combining attempt question + response + question text
  rows: GradingRow[] = [];

  isLoading = false;
  isFinalizing = false;
  activeReviewAction: 'draft' | 'submit' | null = null;
  isApprovingRetake = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  submissionDetailsPartiallyUnavailable = false;
  feedbackDraft = '';
  hasSubmittedReviewLocally = false;

  // Editable score per response id
  scoreEdits: Record<string, { score: number; isCorrect: boolean }> = {};
  savingResponse: Record<string, boolean> = {};
  fileScoreEdits: Record<string, { score: number; isCorrect: boolean }> = {};
  savingFileResponse: Record<string, boolean> = {};
  questionCorrectForms: Record<string, FormGroup> = {};
  fileCorrectForms: Record<string, FormGroup> = {};

  readonly baseUrl = environment.baseUrl;

  assessmentType: 'QUIZ' | 'EXAM' | 'ASSIGNMENT' | null = null;
  markerRole: 'FIRST_MARKER' | 'SECOND_MARKER' = 'FIRST_MARKER';
  instructorId = '';
  private routerDebugAttached = false;
  private windowErrorListener?: (event: ErrorEvent) => void;
  private windowRejectionListener?: (event: PromiseRejectionEvent) => void;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private route: ActivatedRoute,
    private router: Router,
    private assessmentService: AssessmentService,
    private instructorService: InstructorService,
    private translationService: TranslationService,
  ) {}

  private t(key: string): string { return this.translationService.translate(key); }

  private debugLog(message: string, data?: unknown): void {
    console.log(`[GradingComponent] ${message}`, data);
  }

  private attachDebugListeners(): void {
    if (!this.windowErrorListener) {
      this.windowErrorListener = (event: ErrorEvent) => {
        console.error('[GradingComponent] window error', {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error,
        });
      };
      window.addEventListener('error', this.windowErrorListener);
    }

    if (!this.windowRejectionListener) {
      this.windowRejectionListener = (event: PromiseRejectionEvent) => {
        console.error('[GradingComponent] unhandled promise rejection', event.reason);
      };
      window.addEventListener('unhandledrejection', this.windowRejectionListener);
    }

    if (!this.routerDebugAttached) {
      this.routerDebugAttached = true;
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.debugLog('router navigation start', { url: event.url });
        }
        if (event instanceof NavigationEnd) {
          this.debugLog('router navigation end', { url: event.urlAfterRedirects });
        }
        if (event instanceof NavigationError) {
          console.error('[GradingComponent] router navigation error', {
            url: event.url,
            error: event.error,
          });
        }
      });
    }
  }

  get basePath(): string {
    if (this.assessmentType === 'EXAM') return '/main/exams';
    if (this.assessmentType === 'ASSIGNMENT') return '/main/homework';
    return '/main/quizzes';
  }

  get listTitleKey(): string {
    if (this.assessmentType === 'EXAM') return 'Exams';
    if (this.assessmentType === 'ASSIGNMENT') return 'Homework';
    return 'Quizzes';
  }

  get isFromCorrections(): boolean {
    return this.route.snapshot.queryParamMap.get('fromCorrections') === 'true';
  }

  get correctionsBasePath(): string {
    return '/main/assessment/marking';
  }

  get correctionsListTitleKey(): string {
    return this.t('Assessment Marking');
  }

  get detailBreadcrumbLabel(): string {
    if (this.assessment?.nameEn?.trim()) {
      return this.assessment.nameEn;
    }
    if (this.assessmentType === 'EXAM') return this.t('Exam Details');
    if (this.assessmentType === 'ASSIGNMENT') return this.t('Homework Details');
    return this.t('Quiz Details');
  }

  get assessmentTypeLabel(): string {
    if (this.assessmentType === 'EXAM') return this.t('Exam');
    if (this.assessmentType === 'ASSIGNMENT') return this.t('Homework');
    return this.t('Quiz');
  }

  get isHomeworkAssessment(): boolean {
    return this.assessmentType === 'ASSIGNMENT' || this.assessment?.type === 'ASSIGNMENT';
  }

  get homeworkInstructionAttachments(): HomeworkAttachmentDto[] {
    if (!this.isHomeworkAssessment) {
      return [];
    }

    return this.attachments.filter((attachment) =>
      !!attachment.filePath
      || !!attachment.fileName?.trim()
      || !!attachment.instructionsEn?.trim()
      || this.normalizeAllowedSubmissionFormats(attachment.allowedSubmissionFormats).length > 0);
  }

  get markerRoleLabel(): string {
    if (this.isReleasedOfficialResult) return this.t('Official Released Result');
    return this.markerRole === 'SECOND_MARKER'
      ? this.t('Second Marker')
      : this.t('First Marker');
  }

  get submissionMetricCards(): MetricCardItem[] {
    return [
      {
        label: this.t('Status'),
        value: this.formatStatus(this.result?.status ?? 'PENDING'),
        description: this.t('Current submission workflow state'),
        icon: 'ri-flag-line',
        color: this.getSubmissionStatusColor(this.result?.status),
      },
      {
        label: this.t('Score'),
        value: this.liveOverallScoreDisplay,
        description: this.t('Live review total before submission'),
        icon: 'ri-bar-chart-box-line',
        color: '#333fff',
      },
      {
        label: this.t('Questions'),
        value: this.rows.length,
        description: this.t('Assessment questions in this attempt'),
        icon: 'ri-file-list-3-line',
        color: '#10b981',
      },
      {
        label: this.t('File Questions'),
        value: this.fileResponses.length,
        description: this.t('Attached file response items'),
        icon: 'ri-attachment-2',
        color: '#8b5cf6',
      },
    ];
  }

  ngOnInit(): void
  {
    this.attachDebugListeners();
    this.assessmentType = this.resolveAssessmentTypeFromRoute();
    const role = this.route.snapshot.queryParamMap.get('role');
    if (role === 'SECOND_MARKER') {
      this.markerRole = 'SECOND_MARKER';
    }
    this.assessmentId = this.route.snapshot.paramMap.get('id') ?? '';
    this.resultId = this.route.snapshot.paramMap.get('resultId') ?? '';
    if (!this.assessmentId || !this.resultId) { return; }
    this.instructorService.getMe().subscribe({
      next: (instructor) => {
        this.instructorId = instructor.id || instructor.userId;
        this.load();
      },
      error: () => this.load(),
    });
  }

  private resolveAssessmentTypeFromRoute(): 'QUIZ' | 'EXAM' | 'ASSIGNMENT' | null {
    let snapshot: import('@angular/router').ActivatedRouteSnapshot | null = this.route.snapshot;
    while (snapshot) {
      const value = snapshot.data?.['assessmentType'];
      if (value === 'QUIZ' || value === 'EXAM' || value === 'ASSIGNMENT') return value;
      snapshot = snapshot.parent;
    }
    return null;
  }

  private load(): void
  {
    this.isLoading = true;
    this.errorMessage = null;
    this.submissionDetailsPartiallyUnavailable = false;
    this.hasSubmittedReviewLocally = false;
    forkJoin({
      assessment: this.assessmentService.getById(this.assessmentId),
      result: this.assessmentService.getSubmissionInstructor(this.resultId),
      assessmentQuestions: this.assessmentService.getAssessmentQuestions(this.assessmentId).pipe(
        catchError(() => {
          this.submissionDetailsPartiallyUnavailable = true;
          return of([] as AssessmentQuestionDto[]);
        }),
      ),
      attemptQuestions: this.assessmentService.getAttemptQuestions(this.resultId).pipe(
        catchError(() => {
          this.submissionDetailsPartiallyUnavailable = true;
          return of([]);
        }),
      ),
      responses: this.assessmentService.getResponsesByResult(this.resultId, this.markerRole).pipe(
        catchError(() => {
          this.submissionDetailsPartiallyUnavailable = true;
          return of([]);
        }),
      ),
      fileResponses: this.assessmentService.getFileResponsesByResult(this.resultId, this.markerRole).pipe(
        catchError(() => {
          this.submissionDetailsPartiallyUnavailable = true;
          return of([]);
        }),
      ),
      attachments: this.assessmentService.getHomeworkAttachments(this.assessmentId).pipe(
        catchError(() => {
          this.submissionDetailsPartiallyUnavailable = true;
          return of([]);
        }),
      )
    }).subscribe({
      next: ({ assessment, result, assessmentQuestions, attemptQuestions, responses, fileResponses, attachments }) =>
      {
        this.debugLog('load: received grading payload', {
          assessmentId: this.assessmentId,
          resultId: this.resultId,
          markerRole: this.markerRole,
          attemptQuestionsCount: attemptQuestions.length,
          responsesCount: responses.length,
          fileResponsesCount: fileResponses.length,
        });
        this.assessment = assessment;
        this.result = result;
        const normalizedAttemptQuestions = this.mergeAttemptQuestionsWithAssessmentQuestions(
          attemptQuestions,
          assessmentQuestions,
        );
        const markerRoleToLoad =
          result.releasedAt && result.officialMarkerRole
            ? result.officialMarkerRole
            : this.markerRole;
        this.attemptQuestions = normalizedAttemptQuestions;
        this.feedbackDraft = this.isReleasedOfficialResultFor(result)
          ? (result.officialFeedback ?? result.feedback ?? '')
          : (markerRoleToLoad === 'SECOND_MARKER'
              ? result.secondMarkerFeedback ?? ''
              : result.firstMarkerFeedback ?? result.feedback ?? '');
        if (markerRoleToLoad !== this.markerRole) {
          forkJoin({
            responses: this.assessmentService.getResponsesByResult(this.resultId, markerRoleToLoad).pipe(
              catchError(() => {
                this.submissionDetailsPartiallyUnavailable = true;
                return of([] as LearnerQuestionResponseDto[]);
              }),
            ),
            fileResponses: this.assessmentService.getFileResponsesByResult(this.resultId, markerRoleToLoad).pipe(
              catchError(() => {
                this.submissionDetailsPartiallyUnavailable = true;
                return of([] as LearnerFileQuestionResponseDto[]);
              }),
            ),
          }).subscribe(({ responses: officialResponses, fileResponses: officialFileResponses }) => {
            this.debugLog('load: switching to official marker payload', {
              markerRoleToLoad,
              officialResponsesCount: officialResponses.length,
              officialFileResponsesCount: officialFileResponses.length,
            });
            this.responses = officialResponses;
            this.fileResponses = officialFileResponses;
            this.attachments = attachments;
            this.initializeFileScoreEdits(officialFileResponses);
            this.finishBuildingRows(normalizedAttemptQuestions, officialResponses);
          });
          return;
        }
        this.attemptQuestions = normalizedAttemptQuestions;
        this.responses = responses;
        this.fileResponses = fileResponses;
        this.attachments = attachments;
        this.initializeFileScoreEdits(fileResponses);

        this.finishBuildingRows(normalizedAttemptQuestions, responses);
      },
      error: () => { this.isLoading = false; this.errorMessage = this.t('Failed to load submission'); }
    });
  }

  private finishBuildingRows(
    attemptQuestions: LearnerAttemptQuestionDto[],
    responses: LearnerQuestionResponseDto[],
  ): void {
    const questionIds = attemptQuestions.map(aq => aq.questionBankItemId);
    if (questionIds.length === 0)
    {
      this.buildRows(attemptQuestions, responses, []);
      this.isLoading = false;
      return;
    }

    const questionRequests = questionIds.map((id) =>
      forkJoin({
        question: this.assessmentService.getQuestionById(id).pipe(
          catchError(() => of(null as QuestionBankItemDto | null)),
        ),
        answerOptions: this.assessmentService.getAnswerOptions(id).pipe(
          catchError(() => of([] as AnswerOptionDto[])),
        ),
      }),
    );

    forkJoin(questionRequests).subscribe({
      next: (questionBundles) =>
      {
        const questions: QuestionBankItemDto[] = questionBundles
          .flatMap(({ question, answerOptions }) => (
            question
              ? [{ ...question, answerOptions }]
              : []
          ));
        this.buildRows(attemptQuestions, responses, questions);
        this.isLoading = false;
      },
      error: () =>
      {
        this.buildRows(attemptQuestions, responses, []);
        this.isLoading = false;
      }
    });
  }

  private buildRows(
    attemptQuestions: LearnerAttemptQuestionDto[],
    responses: LearnerQuestionResponseDto[],
    questions: QuestionBankItemDto[]
  ): void
  {
    const responseMap: Record<string, LearnerQuestionResponseDto> = {};
    responses.forEach(r => { responseMap[r.questionBankItemId] = r; });

    const questionMap: Record<string, QuestionBankItemDto> = {};
    questions.forEach(q => { questionMap[q.id] = q; });

    this.rows = [...attemptQuestions]
      .sort((a, b) => this.compareAttemptQuestionsForGrading(a, b))
      .map((aq, i) =>
      {
        const q = questionMap[aq.questionBankItemId];
        const r = responseMap[aq.questionBankItemId];
        return {
          orderNum: i + 1,
          attemptQuestion: aq,
          question: q ?? null,
          response: r ?? null,
          hadOriginalResponse: !!r,
        };
      });

    this.initializeQuestionScoreEdits(this.rows);
  }

  private compareAttemptQuestionsForGrading(
    a: LearnerAttemptQuestionDto,
    b: LearnerAttemptQuestionDto,
  ): number {
    const optionalOrder = Number(!!a.optionalQuestion) - Number(!!b.optionalQuestion);
    if (optionalOrder !== 0) {
      return optionalOrder;
    }

    return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
  }

  isManualType(type?: string): boolean
  {
    return type === 'SHORT_ANSWER' || type === 'ESSAY';
  }

  isInstructorEditableQuestionType(type?: string): boolean
  {
    return type === 'MCQ'
      || type === 'TRUE_FALSE'
      || type === 'SHORT_ANSWER'
      || type === 'ESSAY';
  }

  canGrade(): boolean
  {
    if (this.isCurrentMarkerSubmitted) {
      return false;
    }

    return this.result?.status === 'SUBMITTED'
      || this.result?.status === 'WAITING_FOR_APPROVAL';
  }

  formatStatus(status: string): string
  {
    return status
      .toLowerCase()
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  private getSubmissionStatusColor(status?: string | null): string {
    switch (status) {
      case 'APPROVED':
      case 'COMPLETED':
      case 'PASSED':
        return '#10b981';
      case 'FAILED':
      case 'REJECTED':
        return '#ef4444';
      case 'WAITING_FOR_APPROVAL':
        return '#64748b';
      case 'PARTIALLY_GRADED':
        return '#f97316';
      case 'IN_PROGRESS':
        return '#3b82f6';
      case 'SUBMITTED':
      case 'PENDING':
      default:
        return '#f59e0b';
    }
  }

  hasDisplayRichContent(value: string | null | undefined): boolean {
    const cacheKey = value ?? '';
    const cached = this.richContentCache.get(cacheKey);
    if (cached != null) {
      return cached;
    }

    const result = this.hasFormulaOrHtml(this.renderDisplayHtml(value));
    this.richContentCache.set(cacheKey, result);
    return result;
  }

  renderDisplayHtml(value: string | null | undefined): string {
    const cacheKey = value ?? '';
    const cached = this.displayContentCache.get(cacheKey);
    if (cached != null) {
      return cached;
    }

    const rendered = this.normalizeDisplayContent(value);
    this.displayContentCache.set(cacheKey, rendered);
    return rendered;
  }

  getSelectedOptionDisplay(row: GradingRow): string {
    const response = row.response;
    if (!response) {
      return '';
    }

    const answerText = response.answerText?.trim();
    if (answerText) {
      return answerText;
    }

    const selectedOptionIds = this.getSelectedOptionIds(response);
    if (!selectedOptionIds.length) {
      return '';
    }

    return selectedOptionIds
      .map((selectedOptionId) => {
        const selectedOption = row.question?.answerOptions?.find((option) => option.id === selectedOptionId);
        return selectedOption?.optionTextEn?.trim() || selectedOptionId;
      })
      .join(', ');
  }

  getObjectiveAnswerDisplay(row: GradingRow): string {
    const selectedOptionDisplay = this.getSelectedOptionDisplay(row);
    if (selectedOptionDisplay) {
      return selectedOptionDisplay;
    }

    if (row.hadOriginalResponse && row.response && (
      this.getVisibleQuestionCorrectness(row.response) != null
      || this.getVisibleQuestionScore(row.response, row.attemptQuestion.marks) != null
    )) {
      return this.t('(graded answer record unavailable)');
    }

    return this.t('(no answer provided)');
  }

  isMultiSelectQuestion(row: GradingRow): boolean {
    return row.question?.type === 'MCQ' && !!row.question?.allowMultipleSelection;
  }

  isMcqQuestion(row: GradingRow): boolean {
    return row.question?.type === 'MCQ';
  }

  getMcqOptions(row: GradingRow): AnswerOptionDto[] {
    return row.question?.answerOptions ?? [];
  }

  isMcqOptionSelected(row: GradingRow, optionId: string): boolean {
    return !!optionId && this.getSelectedOptionIds(row.response ?? {}).includes(optionId);
  }

  getMcqOptionText(option: AnswerOptionDto): string {
    return option.optionTextEn?.trim() || option.optionTextNative?.trim() || option.id;
  }

  private getSelectedOptionIds(response: Partial<LearnerQuestionResponseDto> | null | undefined): string[] {
    if (!response) {
      return [];
    }

    const selectedOptionIds = (response.selectedOptionIds ?? [])
      .map((optionId) => optionId?.trim())
      .filter((optionId): optionId is string => !!optionId);

    if (selectedOptionIds.length) {
      return selectedOptionIds;
    }

    const selectedOptionId = response.selectedOptionId?.trim();
    return selectedOptionId ? [selectedOptionId] : [];
  }

  getScoreEditKey(row: GradingRow): string {
    return row.response?.id ?? `pending:${row.attemptQuestion.questionBankItemId}`;
  }

  trackByRow = (_index: number, row: GradingRow): string =>
    row.attemptQuestion.id || row.response?.id || row.attemptQuestion.questionBankItemId;

  trackByFileResponse = (_index: number, response: LearnerFileQuestionResponseDto): string =>
    response.id;

  trackByOption = (_index: number, option: AnswerOptionDto): string =>
    option.id;

  trackByHomeworkInstruction = (_index: number, attachment: HomeworkAttachmentDto): string =>
    attachment.id;

  getQuestionScoreEdit(row: GradingRow): { score: number; isCorrect: boolean } | null {
    return this.scoreEdits[this.getScoreEditKey(row)] ?? null;
  }

  getQuestionCorrectForm(row: GradingRow): FormGroup {
    return this.questionCorrectForms[this.getScoreEditKey(row)];
  }

  updateQuestionScore(row: GradingRow, score: number): void {
    const edit = this.ensureQuestionScoreEdit(row);
    edit.score = Number(score ?? 0);
  }

  updateQuestionCorrectness(row: GradingRow, isCorrect: boolean): void {
    const edit = this.ensureQuestionScoreEdit(row);
    this.debugLog('checkbox: question mark-as-correct toggled', {
      questionBankItemId: row.attemptQuestion.questionBankItemId,
      responseId: row.response?.id ?? null,
      previousIsCorrect: edit.isCorrect,
      nextIsCorrect: !!isCorrect,
      score: edit.score,
    });
    edit.isCorrect = !!isCorrect;
    this.syncQuestionCorrectForm(row, edit.isCorrect);
  }

  getFileScoreEdit(response: LearnerFileQuestionResponseDto): { score: number; isCorrect: boolean } | null {
    return this.fileScoreEdits[response.id] ?? null;
  }

  getFileCorrectForm(response: LearnerFileQuestionResponseDto): FormGroup {
    return this.fileCorrectForms[response.id];
  }

  updateFileScore(response: LearnerFileQuestionResponseDto, score: number): void {
    const edit = this.ensureFileScoreEdit(response);
    edit.score = Number(score ?? 0);
  }

  updateFileCorrectness(response: LearnerFileQuestionResponseDto, isCorrect: boolean): void {
    const edit = this.ensureFileScoreEdit(response);
    this.debugLog('checkbox: file mark-as-correct toggled', {
      fileResponseId: response.id,
      homeworkAttachmentId: response.homeworkAttachmentId,
      previousIsCorrect: edit.isCorrect,
      nextIsCorrect: !!isCorrect,
      score: edit.score,
      visibleScore: this.getVisibleFileScore(response, this.getFileQuestionMaxMarks(response)),
      liveScoreBeforeToggle: this.getLiveFileScore(response),
    });
    edit.isCorrect = !!isCorrect;
    this.debugLog('checkbox: file mark-as-correct applied', {
      fileResponseId: response.id,
      currentEdit: this.fileScoreEdits[response.id],
      canFinalize: this.canFinalize(),
      allRowsScored: this.allRowsScored(),
    });
    this.syncFileCorrectForm(response, edit.isCorrect);
  }

  private getInitialEditableQuestionScore(row: GradingRow): number {
    const response = row.response;
    if (!response) {
      return 0;
    }

    if (!this.isManualType(row.question?.type)) {
      if (response.computedIsCorrect === true) {
        return Number(row.attemptQuestion.marks ?? 0);
      }

      if (response.computedIsCorrect === false) {
        return 0;
      }

      return this.normalizeScoreToMarks(
        response.score ?? response.computedScore ?? null,
        row.attemptQuestion.marks,
      ) ?? 0;
    }

    return this.normalizeScoreToMarks(response.score ?? response.computedScore ?? null, row.attemptQuestion.marks) ?? 0;
  }

  private getInitialEditableQuestionCorrectness(row: GradingRow): boolean {
    const response = row.response;
    if (!response) {
      return false;
    }

    if (!this.isManualType(row.question?.type)) {
      return response.computedIsCorrect ?? response.isCorrect ?? false;
    }

    return response.isCorrect ?? response.computedIsCorrect ?? false;
  }

  saveScore(row: GradingRow): void
  {
    const saveKey = this.getScoreEditKey(row);
    if (this.savingResponse[saveKey]) { return; }
    this.savingResponse[saveKey] = true;
    const edit = this.scoreEdits[saveKey];
    this.createOrUpdateQuestionResponse(row).subscribe({
      next: (updated) =>
      {
        this.syncUpdatedRowResponse(row, updated);
        delete this.savingResponse[saveKey];
        this.successMessage = this.t('Score saved');
        setTimeout(() => this.successMessage = null, 4000);
      },
      error: () =>
      {
        delete this.savingResponse[saveKey];
        this.errorMessage = this.t('Failed to save score');
      }
    });
  }

  getAttachmentName(homeworkAttachmentId: string): string
  {
    return this.attachments.find(a => a.id === homeworkAttachmentId)?.fileName ?? homeworkAttachmentId;
  }

  getAllowedSubmissionFormats(attachment: HomeworkAttachmentDto): string[] {
    return this.normalizeAllowedSubmissionFormats(attachment.allowedSubmissionFormats);
  }

  private normalizeAllowedSubmissionFormats(formats: string[] | string | null | undefined): string[] {
    if (!formats) {
      return [];
    }

    const values = Array.isArray(formats) ? formats : formats.split(',');
    return values
      .map((format) => format.trim())
      .filter((format) => !!format);
  }

  getAttachmentMarks(homeworkAttachmentId: string): number
  {
    return this.attachments.find(a => a.id === homeworkAttachmentId)?.marks ?? 0;
  }

  getFileQuestionMaxMarks(fileResponse: LearnerFileQuestionResponseDto): number
  {
    const attachmentMarks = this.getAttachmentMarks(fileResponse.homeworkAttachmentId);
    if (attachmentMarks > 0) {
      return attachmentMarks;
    }

    const missingMarkedResponses = this.fileResponses.filter((response) =>
      this.getAttachmentMarks(response.homeworkAttachmentId) <= 0,
    );
    if (missingMarkedResponses.length === 0) {
      return 0;
    }

    const assessmentTotalMarks = this.assessment?.totalMarks ?? 0;
    const attemptQuestionMarksTotal = this.attemptQuestions.reduce(
      (sum, question) => sum + (question.marks ?? 0),
      0,
    );
    const knownFileQuestionMarksTotal = this.fileResponses.reduce(
      (sum, response) => sum + Math.max(this.getAttachmentMarks(response.homeworkAttachmentId), 0),
      0,
    );
    const remainingMarks = Math.max(
      assessmentTotalMarks - attemptQuestionMarksTotal - knownFileQuestionMarksTotal,
      0,
    );

    if (remainingMarks <= 0) {
      return 0;
    }

    return remainingMarks / missingMarkedResponses.length;
  }

  get displayedOverallScore(): number
  {
    if (this.isReleasedOfficialResult) {
      return this.normalizeOverallScoreToMarks(this.result?.officialScore ?? this.result?.score ?? 0);
    }
    const awardedQuestionMarks = this.rows.reduce(
      (sum, row) => sum + (this.getVisibleQuestionScore(row.response, row.attemptQuestion.marks) ?? 0),
      0,
    );
    const awardedFileMarks = this.fileResponses.reduce(
      (sum, response) => sum + (this.getVisibleFileScore(response, this.getFileQuestionMaxMarks(response)) ?? 0),
      0,
    );
    return awardedQuestionMarks + awardedFileMarks;
  }

  get liveDisplayedOverallScore(): number {
    if (this.isReleasedOfficialResult || this.isAlreadyFinalized) {
      return this.displayedOverallScore;
    }

    const awardedQuestionMarks = this.rows.reduce(
      (sum, row) => sum + (this.getLiveQuestionScore(row) ?? 0),
      0,
    );
    const awardedFileMarks = this.fileResponses.reduce(
      (sum, response) => sum + (this.getLiveFileScore(response) ?? 0),
      0,
    );
    return awardedQuestionMarks + awardedFileMarks;
  }

  get liveBaseScore(): number {
    return Math.min(this.liveMandatoryScore, this.baseScoreTotalMarks);
  }

  get liveBonusScore(): number {
    return Math.max(this.liveDisplayedOverallScore - (this.assessment?.totalMarks ?? 0), 0);
  }

  get liveOverallScoreDisplay(): string {
    return this.formatMarksWithBonus(this.liveDisplayedOverallScore, this.assessment?.totalMarks ?? 0);
  }

  get liveMandatoryScore(): number {
    const awardedMandatoryQuestionMarks = this.rows.reduce(
      (sum, row) => sum + (!row.attemptQuestion.optionalQuestion ? (this.getLiveQuestionScore(row) ?? 0) : 0),
      0,
    );
    const awardedFileMarks = this.fileResponses.reduce(
      (sum, response) => sum + (this.getLiveFileScore(response) ?? 0),
      0,
    );
    return awardedMandatoryQuestionMarks + awardedFileMarks;
  }

  get mandatoryQuestionMarks(): number {
    const total = this.rows.reduce(
      (sum, row) => sum + (!row.attemptQuestion.optionalQuestion ? (row.attemptQuestion.marks ?? 0) : 0),
      0,
    );
    return total > 0 ? total : this.assessment?.totalMarks ?? 0;
  }

  get baseScoreTotalMarks(): number {
    return this.mandatoryQuestionMarks || this.assessment?.totalMarks || 0;
  }

  get optionalQuestionCount(): number {
    return this.rows.filter((row) => row.attemptQuestion.optionalQuestion).length;
  }

  get optionalQuestionMarks(): number {
    return this.rows.reduce(
      (sum, row) => sum + (row.attemptQuestion.optionalQuestion ? (row.attemptQuestion.marks ?? 0) : 0),
      0,
    );
  }

  get optionalQuestionsLiveScore(): number {
    return this.rows.reduce(
      (sum, row) => sum + (row.attemptQuestion.optionalQuestion ? (this.getLiveQuestionScore(row) ?? 0) : 0),
      0,
    );
  }

  saveFileScore(fr: LearnerFileQuestionResponseDto): void
  {
    if (this.savingFileResponse[fr.id]) { return; }
    this.savingFileResponse[fr.id] = true;
    const edit = this.fileScoreEdits[fr.id];
    this.assessmentService
      .updateFileResponse(fr.id, {
        score: edit.score,
        isCorrect: edit.isCorrect,
        markerRole: this.markerRole,
      })
      .subscribe({
        next: (updated) =>
        {
          const idx = this.fileResponses.findIndex(r => r.id === updated.id);
          if (idx >= 0) { this.fileResponses[idx] = updated; }
          this.savingFileResponse[updated.id] = false;
          this.successMessage = this.t('Score saved');
          setTimeout(() => this.successMessage = null, 4000);
        },
        error: () =>
        {
          this.savingFileResponse[fr.id] = false;
          this.errorMessage = this.t('Failed to save score');
        }
      });
  }

  allRowsScored(): boolean
  {
    this.syncCorrectnessEditsFromForms();
    const bankScored = this.rows.every(row =>
      row.response == null || this.getLiveQuestionScore(row) != null
    );
    const fileScored = this.fileResponses.every(fr => this.getLiveFileScore(fr) != null);
    return bankScored && fileScored;
  }

  canFinalize(): boolean
  {
    this.syncCorrectnessEditsFromForms();
    return (
      this.result?.status === 'SUBMITTED'
      || this.result?.status === 'WAITING_FOR_APPROVAL'
    ) && this.allRowsScored() && !this.hasInvalidDraftScores();
  }

  canSaveDraft(): boolean
  {
    return this.canGrade() && !this.hasInvalidDraftScores();
  }

  get saveDraftButtonLabel(): string
  {
    if (this.activeReviewAction === 'draft') {
      return this.t('Saving...');
    }
    return this.t('Save Draft');
  }

  get submitButtonLabel(): string
  {
    if (this.activeReviewAction === 'submit') {
      return this.t('Submitting...');
    }
    return this.t('Submit Review');
  }

  get isAlreadyFinalized(): boolean
  {
    return this.result?.status === 'APPROVED' || this.result?.status === 'COMPLETED';
  }

  get isCurrentMarkerSubmitted(): boolean
  {
    if (this.isReleasedOfficialResult) {
      return true;
    }

    if (this.hasSubmittedReviewLocally) {
      return true;
    }

    if (this.markerRole === 'SECOND_MARKER') {
      return !!this.result?.secondMarkerGradedAt;
    }

    return !!this.result?.firstMarkerGradedAt;
  }

  get currentMarkerSubmittedAt(): string | null
  {
    if (this.markerRole === 'SECOND_MARKER') {
      return this.result?.secondMarkerGradedAt ?? null;
    }

    return this.result?.firstMarkerGradedAt ?? null;
  }

  get isReleasedOfficialResult(): boolean
  {
    return this.isReleasedOfficialResultFor(this.result);
  }

  get officialSelectedMarkerName(): string
  {
    if (!this.assessment || !this.result?.officialMarkerRole) {
      return this.t('Pending');
    }
    if (this.result.officialMarkerRole === 'SECOND_MARKER') {
      return this.assessment.secondMarkerName
        ?? this.assessment.secondMarkerId
        ?? this.t('Second Marker');
    }
    return this.assessment.firstMarkerName
      ?? this.assessment.instructorName
      ?? this.assessment.firstMarkerId
      ?? this.assessment.instructorId
      ?? this.t('First Marker');
  }

  get visibleReviewComment(): string
  {
    if (this.isReleasedOfficialResult) {
      return this.result?.officialFeedback ?? this.result?.feedback ?? '';
    }
    if (this.markerRole === 'SECOND_MARKER') {
      return this.result?.secondMarkerFeedback ?? this.result?.feedback ?? '';
    }
    return this.result?.firstMarkerFeedback ?? this.result?.feedback ?? '';
  }

  private isReleasedOfficialResultFor(result: LearnerAssessmentResultDto | null): boolean {
    return !!result?.releasedAt && result?.status === 'COMPLETED';
  }

  get hasPendingRetakeRequest(): boolean
  {
    return false;
  }

  exceedsQuestionMarks(responseId: string, maxMarks: number): boolean
  {
    const score = this.scoreEdits[responseId]?.score;
    return score != null && Number(score) > Number(maxMarks);
  }

  exceedsFileQuestionMarks(responseId: string, maxMarks: number): boolean
  {
    const score = this.fileScoreEdits[responseId]?.score;
    return score != null && Number(score) > Number(maxMarks);
  }

  private hasInvalidDraftScores(): boolean {
    const manualScoresInvalid = this.rows.some((row) => {
      if (!this.isInstructorEditableQuestionType(row.question?.type)) {
        return false;
      }
      const score = this.scoreEdits[this.getScoreEditKey(row)]?.score;
      return this.isInvalidScore(score, row.attemptQuestion.marks ?? 0);
    });

    if (manualScoresInvalid) {
      return true;
    }

    return this.fileResponses.some((response) => {
      const score = this.fileScoreEdits[response.id]?.score;
      return this.isInvalidScore(score, this.getFileQuestionMaxMarks(response));
    });
  }

  private mergeAttemptQuestionsWithAssessmentQuestions(
    attemptQuestions: LearnerAttemptQuestionDto[],
    assessmentQuestions: AssessmentQuestionDto[],
  ): LearnerAttemptQuestionDto[] {
    if (!attemptQuestions.length || !assessmentQuestions.length) {
      return attemptQuestions;
    }

    const assessmentQuestionMap = new Map(
      assessmentQuestions.map((question) => [question.questionId, question]),
    );

    return attemptQuestions.map((attemptQuestion) => {
      const assessmentQuestion = assessmentQuestionMap.get(attemptQuestion.questionBankItemId);
      if (!assessmentQuestion) {
        return attemptQuestion;
      }

      return {
        ...attemptQuestion,
        marks: assessmentQuestion.marks ?? attemptQuestion.marks,
        orderIndex: assessmentQuestion.orderIndex ?? attemptQuestion.orderIndex,
        optionalQuestion: assessmentQuestion.optionalQuestion ?? attemptQuestion.optionalQuestion,
      };
    });
  }

  private isInvalidScore(score: number | null | undefined, maxMarks: number): boolean {
    if (score == null) {
      return false;
    }

    const numericScore = Number(score);
    return !Number.isFinite(numericScore) || numericScore < 0 || numericScore > Number(maxMarks);
  }

  getVisibleQuestionScore(
    response: LearnerQuestionResponseDto | null | undefined,
    maxMarks?: number | null,
  ): number | null
  {
    if (!response) {
      return null;
    }
    return this.normalizeScoreToMarks(response.score ?? response.computedScore ?? null, maxMarks);
  }

  getVisibleQuestionCorrectness(response: LearnerQuestionResponseDto | null | undefined): boolean | null
  {
    if (!response) {
      return null;
    }
    return response.isCorrect ?? response.computedIsCorrect ?? null;
  }

  getVisibleFileScore(
    response: LearnerFileQuestionResponseDto | null | undefined,
    maxMarks?: number | null,
  ): number | null
  {
    if (!response) {
      return null;
    }
    return this.normalizeScoreToMarks(response.score ?? null, maxMarks);
  }

  getLiveQuestionScore(row: GradingRow): number | null {
    if (!row.response) {
      if (this.isInstructorEditableQuestionType(row.question?.type)) {
        const draftScore = this.scoreEdits[this.getScoreEditKey(row)]?.score;
        if (draftScore != null && Number.isFinite(Number(draftScore))) {
          return Number(draftScore);
        }
      }
      return null;
    }

    if (!this.isReleasedOfficialResult && !this.isAlreadyFinalized && this.isInstructorEditableQuestionType(row.question?.type)) {
      const draftScore = this.scoreEdits[this.getScoreEditKey(row)]?.score;
      if (draftScore != null && Number.isFinite(Number(draftScore))) {
        return Number(draftScore);
      }
    }

    const visibleScore = this.getVisibleQuestionScore(row.response, row.attemptQuestion.marks);
    if (visibleScore != null) {
      return visibleScore;
    }

    // Auto-graded questions can come back with correctness but no explicit score.
    // Treat answered incorrect/correct responses as scored so the review is not stuck in "Pending".
    if (!this.isInstructorEditableQuestionType(row.question?.type) && row.response) {
      return 0;
    }

    return null;
  }

  getLiveFileScore(response: LearnerFileQuestionResponseDto | null | undefined): number | null {
    if (!response) {
      return null;
    }

    if (!this.isReleasedOfficialResult && !this.isAlreadyFinalized) {
      const draftScore = this.fileScoreEdits[response.id]?.score;
      if (draftScore != null && Number.isFinite(Number(draftScore))) {
        return Number(draftScore);
      }
    }

    return this.getVisibleFileScore(response, this.getFileQuestionMaxMarks(response));
  }

  private normalizeOverallScoreToMarks(score: number | null | undefined): number {
    return this.normalizeScoreToMarks(score, this.assessment?.totalMarks ?? 0) ?? 0;
  }

  private normalizeScoreToMarks(score: number | null | undefined, maxMarks?: number | null): number | null {
    return normalizeAssessmentScoreToMarks(score, this.assessment?.totalMarks ?? 0, maxMarks);
  }

  private formatMarksWithBonus(score: number | null | undefined, totalMarks: number | null | undefined): string {
    const numericScore = Number(score ?? 0);
    const numericTotalMarks = Number(totalMarks ?? 0);
    if (!Number.isFinite(numericScore)) {
      return '--';
    }
    if (!Number.isFinite(numericTotalMarks) || numericTotalMarks <= 0) {
      return this.formatScoreNumber(numericScore);
    }

    const baseScore = Math.min(numericScore, numericTotalMarks);
    const bonusScore = Math.max(numericScore - numericTotalMarks, 0);
    const base = `${this.formatScoreNumber(baseScore)} / ${this.formatScoreNumber(numericTotalMarks)}`;
    return bonusScore > 0
      ? `${base} (+${this.formatScoreNumber(bonusScore)} bonus)`
      : base;
  }

  private formatScoreNumber(value: number): string {
    if (!Number.isFinite(value)) {
      return '--';
    }
    return Number.isInteger(value)
      ? `${value}`
      : value.toFixed(2).replace(/\.?0+$/, '');
  }

  finalizeGrade(): void
  {
    if (this.isFinalizing) { return; }
    this.isFinalizing = true;
    this.activeReviewAction = 'submit';
    this.errorMessage = null;
    this.successMessage = null;
    this.persistAllScoreEdits().subscribe({
      next: () => {
        this.assessmentService.saveMarkerReview(this.resultId, {
          markerRole: this.markerRole,
          instructorId: this.instructorId || this.assessment?.instructorId || '',
          feedback: this.feedbackDraft?.trim() || null,
          submit: true,
        }).subscribe({
          next: (updated) =>
          {
            this.result = updated;
            this.hasSubmittedReviewLocally = true;
            this.feedbackDraft = this.markerRole === 'SECOND_MARKER'
              ? updated.secondMarkerFeedback ?? ''
              : updated.firstMarkerFeedback ?? '';
            this.isFinalizing = false;
            this.activeReviewAction = null;
            this.successMessage = this.t('Review submitted');
            setTimeout(() => this.successMessage = null, 5000);
          },
          error: () => {
            this.isFinalizing = false;
            this.activeReviewAction = null;
            this.errorMessage = this.t('Failed to save review');
          }
        });
      },
      error: () => {
        this.isFinalizing = false;
        this.activeReviewAction = null;
        this.errorMessage = this.t('Failed to save score');
      },
    });
  }

  saveDraftReview(): void
  {
    if (this.isFinalizing) { return; }
    this.isFinalizing = true;
    this.activeReviewAction = 'draft';
    this.errorMessage = null;
    this.successMessage = null;
    this.persistAllScoreEdits().subscribe({
      next: () => {
        this.assessmentService.saveMarkerReview(this.resultId, {
          markerRole: this.markerRole,
          instructorId: this.instructorId || this.assessment?.instructorId || '',
          feedback: this.feedbackDraft?.trim() || null,
          submit: false,
        }).subscribe({
          next: (updated) =>
          {
            this.result = updated;
            this.feedbackDraft = this.markerRole === 'SECOND_MARKER'
              ? updated.secondMarkerFeedback ?? ''
              : updated.firstMarkerFeedback ?? updated.feedback ?? '';
            this.isFinalizing = false;
            this.activeReviewAction = null;
            this.successMessage = this.t('Draft saved');
            setTimeout(() => this.successMessage = null, 5000);
          },
          error: () => {
            this.isFinalizing = false;
            this.activeReviewAction = null;
            this.errorMessage = this.t('Failed to save review');
          }
        });
      },
      error: () => {
        this.isFinalizing = false;
        this.activeReviewAction = null;
        this.errorMessage = this.t('Failed to save score');
      },
    });
  }

  private persistAllScoreEdits(): Observable<void> {
    this.syncCorrectnessEditsFromForms();
    this.debugLog('persistAllScoreEdits: starting', {
      manualRows: this.rows.length,
      fileResponses: this.fileResponses.length,
      fileScoreEdits: this.fileScoreEdits,
      fileCorrectFormValues: Object.fromEntries(
        Object.entries(this.fileCorrectForms).map(([key, form]) => [key, form.get('isCorrect')?.value]),
      ),
    });
    const manualRowUpdates = this.rows
      .filter((row) => this.isInstructorEditableQuestionType(row.question?.type))
      .map((row) => this.createOrUpdateQuestionResponse(row));

    const fileUpdates = this.fileResponses.map((response) => {
      const edit = this.fileScoreEdits[response.id];
      return this.assessmentService.updateFileResponse(response.id, {
        score: edit.score,
        isCorrect: edit.isCorrect,
        markerRole: this.markerRole,
      });
    });

    return new Observable<void>((subscriber) => {
      forkJoin([
        manualRowUpdates.length ? forkJoin(manualRowUpdates) : of([] as LearnerQuestionResponseDto[]),
        fileUpdates.length ? forkJoin(fileUpdates) : of([] as LearnerFileQuestionResponseDto[]),
      ]).subscribe({
        next: ([updatedResponses, updatedFileResponses]) => {
          this.debugLog('persistAllScoreEdits: completed', {
            updatedResponsesCount: updatedResponses.length,
            updatedFileResponsesCount: updatedFileResponses.length,
          });
          const updatedResponseMap = new Map(updatedResponses.map((response) => [response.id, response]));
          this.rows = this.rows.map((row) => {
            if (!row.response) {
              return row;
            }
            const updatedResponse = updatedResponseMap.get(row.response.id);
            return updatedResponse ? { ...row, response: updatedResponse } : row;
          });
          this.responses = this.responses.map((response) => updatedResponseMap.get(response.id) ?? response);

          const updatedFileMap = new Map(updatedFileResponses.map((response) => [response.id, response]));
          this.fileResponses = this.fileResponses.map((response) => updatedFileMap.get(response.id) ?? response);

          subscriber.next();
          subscriber.complete();
        },
        error: (error) => {
          console.error('[GradingComponent] persistAllScoreEdits: failed', error);
          subscriber.error(error);
        },
      });
    });
  }

  private createOrUpdateQuestionResponse(row: GradingRow): Observable<LearnerQuestionResponseDto> {
    const edit = this.scoreEdits[this.getScoreEditKey(row)];
    if (row.response) {
      return this.assessmentService.updateResponse(row.response.id, {
        score: edit.score,
        isCorrect: edit.isCorrect,
        markerRole: this.markerRole,
      });
    }

    const learnerId = this.resolveLearnerIdForResponseCreation(row);
    if (!learnerId) {
      return new Observable<LearnerQuestionResponseDto>((subscriber) => {
        subscriber.error(new Error('Learner ID is required'));
      });
    }

    return new Observable<LearnerQuestionResponseDto>((subscriber) => {
      this.assessmentService.createResponse({
        resultId: this.resultId,
        questionBankItemId: row.attemptQuestion.questionBankItemId,
        learnerId,
        isCorrect: edit.isCorrect,
        score: edit.score,
      }).subscribe({
        next: (created) => {
          this.assessmentService.updateResponse(created.id, {
            score: edit.score,
            isCorrect: edit.isCorrect,
            markerRole: this.markerRole,
          }).subscribe({
            next: (updated) => {
              subscriber.next(updated);
              subscriber.complete();
            },
            error: (error) => subscriber.error(error),
          });
        },
        error: (error) => subscriber.error(error),
      });
    });
  }

  private resolveLearnerIdForResponseCreation(row: GradingRow): string {
    return (
      row.response?.learnerId?.trim()
      || this.result?.learnerId?.trim()
      || this.responses.find((response) => response.learnerId?.trim())?.learnerId?.trim()
      || this.fileResponses.find((response) => response.learnerId?.trim())?.learnerId?.trim()
      || ''
    );
  }

  private syncUpdatedRowResponse(row: GradingRow, updated: LearnerQuestionResponseDto): void {
    const previousKey = this.getScoreEditKey(row);
    row.response = updated;
    this.rows = this.rows.map((existingRow) =>
      existingRow.attemptQuestion.id === row.attemptQuestion.id
        ? { ...existingRow, response: updated }
        : existingRow,
    );

    const existingResponseIndex = this.responses.findIndex((response) => response.id === updated.id);
    if (existingResponseIndex >= 0) {
      this.responses[existingResponseIndex] = updated;
    } else {
      this.responses = [...this.responses, updated];
    }

    const nextKey = this.getScoreEditKey(row);
    if (previousKey !== nextKey && this.scoreEdits[previousKey]) {
      this.scoreEdits[nextKey] = this.scoreEdits[previousKey];
      delete this.scoreEdits[previousKey];
    }

    this.initializeQuestionScoreEdits(this.rows);
  }

  approveRetakeRequest(): void
  {
    return;
  }

  goBack(): void
  {
    if (this.isFromCorrections) {
      this.router.navigate([this.correctionsBasePath, this.assessmentId]);
      return;
    }

    this.router.navigate([this.basePath, this.assessmentId]);
  }

  openMedia(path: string | null | undefined): void
  {
    if (!path) {
      return;
    }
    window.open(`${this.baseUrl}/media/${path}`, '_blank', 'noopener');
  }

  private hasFormulaOrHtml(value: string | null | undefined): boolean {
    if (!value) {
      return false;
    }
    return /ql-formula/.test(value) || /<[a-z][\s\S]*>/i.test(value);
  }

  private containsLatexExpression(value: string | null | undefined): boolean {
    return Boolean(value && (/\\[a-zA-Z]+/.test(value) || /(\{[^{}]+\}|[A-Za-z0-9]+)\s*[\^_]/.test(value)));
  }

  private normalizeDisplayContent(value: string | null | undefined): string {
    if (!value || /ql-formula/.test(value) || !this.containsLatexExpression(value)) {
      return value ?? '';
    }

    if (/<[a-z][\s\S]*>/i.test(value)) {
      const container = this.document.createElement('div');
      container.innerHTML = value;
      const converted = this.convertLatexTextNodes(container);
      return converted ? container.innerHTML : value;
    }

    const parts: string[] = [];
    let buffer = '';
    let cursor = 0;
    let converted = false;

    const flushBuffer = (): void => {
      if (!buffer) {
        return;
      }
      parts.push(this.escapeHtml(buffer).replace(/\r?\n/g, '<br>'));
      buffer = '';
    };

    while (cursor < value.length) {
      const latexExpression = this.readFormulaExpression(value, cursor);
      if (latexExpression) {
        flushBuffer();
        parts.push(this.renderFormulaEmbedHtml(latexExpression));
        cursor += latexExpression.length;
        converted = true;
        continue;
      }

      buffer += value[cursor];
      cursor += 1;
    }

    flushBuffer();

    return converted ? `<p>${parts.join('')}</p>` : value;
  }

  private convertLatexTextNodes(root: Node): boolean {
    let converted = false;
    const childNodes = Array.from(root.childNodes);

    childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ?? '';
        if (!this.containsLatexExpression(text)) {
          return;
        }

        const fragment = this.createFormulaFragmentFromText(text);
        if (fragment) {
          child.parentNode?.replaceChild(fragment, child);
          converted = true;
        }
        return;
      }

      if (child.nodeType === Node.ELEMENT_NODE) {
        converted = this.convertLatexTextNodes(child) || converted;
      }
    });

    return converted;
  }

  private createFormulaFragmentFromText(value: string): DocumentFragment | null {
    const fragment = this.document.createDocumentFragment();
    let buffer = '';
    let cursor = 0;
    let converted = false;

    const flushBuffer = (): void => {
      if (!buffer) {
        return;
      }
      fragment.appendChild(this.document.createTextNode(buffer));
      buffer = '';
    };

    while (cursor < value.length) {
      const latexExpression = this.readFormulaExpression(value, cursor);
      if (latexExpression) {
        flushBuffer();
        const wrapper = this.document.createElement('div');
        wrapper.innerHTML = this.renderFormulaEmbedHtml(latexExpression);
        const formulaNode = wrapper.firstChild;
        if (formulaNode) {
          fragment.appendChild(formulaNode);
          converted = true;
        } else {
          buffer += latexExpression;
        }
        cursor += latexExpression.length;
        continue;
      }

      buffer += value[cursor];
      cursor += 1;
    }

    flushBuffer();

    return converted ? fragment : null;
  }

  private readFormulaExpression(value: string, startIndex: number): string | null {
    return this.readLatexExpression(value, startIndex) ?? this.readExponentExpression(value, startIndex);
  }

  private readLatexExpression(value: string, startIndex: number): string | null {
    if (value[startIndex] !== '\\') {
      return null;
    }

    let cursor = startIndex + 1;
    while (cursor < value.length && /[a-zA-Z]/.test(value[cursor])) {
      cursor += 1;
    }

    if (cursor === startIndex + 1) {
      return null;
    }

    let braceDepth = 0;
    while (cursor < value.length) {
      const currentChar = value[cursor];
      if (currentChar === '{') {
        braceDepth += 1;
        cursor += 1;
        continue;
      }
      if (currentChar === '}') {
        if (braceDepth === 0) {
          break;
        }
        braceDepth -= 1;
        cursor += 1;
        continue;
      }
      if (braceDepth === 0 && /\s/.test(currentChar)) {
        break;
      }
      cursor += 1;
    }

    return value.slice(startIndex, cursor);
  }

  private readExponentExpression(value: string, startIndex: number): string | null {
    const remainder = value.slice(startIndex);
    const baseMatch = remainder.match(/^(\{[^{}]+\}|[A-Za-z0-9]+)\s*[\^_]/);
    if (!baseMatch) {
      return null;
    }

    let cursor = startIndex;
    while (cursor < value.length) {
      const currentChar = value[cursor];
      const nextChar = value[cursor + 1] ?? '';
      const nextNextChar = value[cursor + 2] ?? '';

      if (/[A-Za-z0-9{}^_=+\-*/()\\\s]/.test(currentChar)) {
        cursor += 1;
        continue;
      }

      if (currentChar === '.' && /\s/.test(nextChar) && /[A-Za-z]/.test(nextNextChar)) {
        break;
      }

      break;
    }

    const expression = value.slice(startIndex, cursor).trimEnd().replace(/[.,;:!?]+$/, '');
    return /[\^_]/.test(expression) ? expression : null;
  }

  private renderFormulaEmbedHtml(latex: string): string {
    return `<span class="ql-formula" data-value="${this.escapeHtml(latex)}"></span>`;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private ensureQuestionScoreEdit(row: GradingRow): { score: number; isCorrect: boolean } {
    const key = this.getScoreEditKey(row);
    const existing = this.scoreEdits[key];
    if (existing) {
      return existing;
    }

    const created = {
      score: this.getInitialEditableQuestionScore(row),
      isCorrect: this.getInitialEditableQuestionCorrectness(row),
    };
    this.scoreEdits[key] = created;
    this.ensureQuestionCorrectForm(row, created.isCorrect);
    return created;
  }

  private ensureFileScoreEdit(response: LearnerFileQuestionResponseDto): { score: number; isCorrect: boolean } {
    const existing = this.fileScoreEdits[response.id];
    if (existing) {
      return existing;
    }

    const created = {
      score: response.score ?? 0,
      isCorrect: response.isCorrect ?? false,
    };
    this.fileScoreEdits[response.id] = created;
    this.ensureFileCorrectForm(response, created.isCorrect);
    return created;
  }

  private initializeQuestionScoreEdits(rows: GradingRow[]): void {
    const nextEdits: Record<string, { score: number; isCorrect: boolean }> = {};

    rows.forEach((row) => {
      if (!this.isInstructorEditableQuestionType(row.question?.type)) {
        return;
      }

      const key = this.getScoreEditKey(row);
      nextEdits[key] = this.scoreEdits[key] ?? {
        score: this.getInitialEditableQuestionScore(row),
        isCorrect: this.getInitialEditableQuestionCorrectness(row),
      };
      this.ensureQuestionCorrectForm(row, nextEdits[key].isCorrect);
    });

    this.scoreEdits = nextEdits;
  }

  private initializeFileScoreEdits(
    responses: LearnerFileQuestionResponseDto[],
  ): void {
    const nextEdits: Record<string, { score: number; isCorrect: boolean }> = {};

    responses.forEach((response) => {
      nextEdits[response.id] = this.fileScoreEdits[response.id] ?? {
        score: response.score ?? 0,
        isCorrect: response.isCorrect ?? false,
      };
      this.ensureFileCorrectForm(response, nextEdits[response.id].isCorrect);
    });

    this.fileScoreEdits = nextEdits;
  }

  private ensureQuestionCorrectForm(row: GradingRow, isCorrect: boolean): void {
    const key = this.getScoreEditKey(row);
    if (this.questionCorrectForms[key]) {
      this.questionCorrectForms[key].get('isCorrect')?.setValue(isCorrect, { emitEvent: false });
      return;
    }

    const form = new FormGroup({
      isCorrect: new FormControl(!!isCorrect, { nonNullable: true }),
    });
    form.get('isCorrect')?.valueChanges.subscribe((value) => {
      const nextValue = !!value;
      if (this.scoreEdits[key]?.isCorrect === nextValue) {
        return;
      }
      const targetRow = this.rows.find((candidate) => this.getScoreEditKey(candidate) === key);
      if (targetRow) {
        this.updateQuestionCorrectness(targetRow, nextValue);
      }
    });
    this.questionCorrectForms[key] = form;
  }

  private ensureFileCorrectForm(response: LearnerFileQuestionResponseDto, isCorrect: boolean): void {
    if (this.fileCorrectForms[response.id]) {
      this.fileCorrectForms[response.id].get('isCorrect')?.setValue(isCorrect, { emitEvent: false });
      return;
    }

    const form = new FormGroup({
      isCorrect: new FormControl(!!isCorrect, { nonNullable: true }),
    });
    form.get('isCorrect')?.valueChanges.subscribe((value) => {
      const nextValue = !!value;
      if (this.fileScoreEdits[response.id]?.isCorrect === nextValue) {
        return;
      }
      const targetResponse = this.fileResponses.find((candidate) => candidate.id === response.id);
      if (targetResponse) {
        this.updateFileCorrectness(targetResponse, nextValue);
      }
    });
    this.fileCorrectForms[response.id] = form;
  }

  private syncQuestionCorrectForm(row: GradingRow, isCorrect: boolean): void {
    this.ensureQuestionCorrectForm(row, isCorrect);
  }

  private syncFileCorrectForm(response: LearnerFileQuestionResponseDto, isCorrect: boolean): void {
    this.ensureFileCorrectForm(response, isCorrect);
  }

  private syncCorrectnessEditsFromForms(): void {
    this.rows.forEach((row) => {
      const key = this.getScoreEditKey(row);
      const formValue = this.questionCorrectForms[key]?.get('isCorrect')?.value;
      if (formValue == null) {
        return;
      }

      const edit = this.ensureQuestionScoreEdit(row);
      edit.isCorrect = !!formValue;
    });

    this.fileResponses.forEach((response) => {
      const formValue = this.fileCorrectForms[response.id]?.get('isCorrect')?.value;
      if (formValue == null) {
        return;
      }

      const edit = this.ensureFileScoreEdit(response);
      edit.isCorrect = !!formValue;
    });
  }
}

interface GradingRow
{
  orderNum: number;
  attemptQuestion: LearnerAttemptQuestionDto;
  question: QuestionBankItemDto | null;
  response: LearnerQuestionResponseDto | null;
  hadOriginalResponse: boolean;
}

